// src/hooks/usePageWebSocket.ts
import { useEffect, useState, useCallback, useRef } from 'react';
import { pageWebSocket } from '../services/PageWebSocketService';

// Main hook - copy and customize this for different pages
export const usePageWebSocket = (roomId: string, autoConnect = true) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [received, setReceived] = useState<any>(null);
  const handlersRef = useRef(new Set<Function>());

  useEffect(() => {
    if (!autoConnect || !roomId) return;

    let isMounted = true;
    setIsConnecting(true);
    
    // Capture the handlers set at the start of the effect
    const handlers = handlersRef.current;

    const connectToRoom = async () => {
      try {
        await pageWebSocket.connectToRoom(roomId);
        if (isMounted) {
          setIsConnected(true);
          setIsConnecting(false);
        }
      } catch (error) {
        console.error(`Failed to connect to room ${roomId}:`, error);
        if (isMounted) {
          setIsConnected(false);
          setIsConnecting(false);
        }
      }
    };

    connectToRoom();

    // Update connection status periodically
    const statusInterval = setInterval(() => {
      if (isMounted) {
        const state = pageWebSocket.getConnectionState();
        setIsConnected(state.isConnected);
        setIsConnecting(state.isConnecting);
      }
    }, 1000);

    return () => {
      isMounted = false;
      clearInterval(statusInterval);
      
      // Clean up handlers when component unmounts or room changes
      handlers.forEach(handler => {
        pageWebSocket.unsubscribe(handler as any);
      });
      handlers.clear();
    };
  }, [roomId, autoConnect]);

  // Subscribe to messages
  const subscribe = useCallback((handler: (data: any) => void) => {
    pageWebSocket.subscribe(handler);
    handlersRef.current.add(handler);
    
    return () => {
      pageWebSocket.unsubscribe(handler);
      handlersRef.current.delete(handler);
    };
  }, []);

  // Send message
  const sendMessage = useCallback((data: any) => {
    pageWebSocket.sendMessage(data);
  }, []);

  // Auto-subscribe to receive latest message
  useEffect(() => {
    const messageHandler = (data: any) => {
      setReceived(data);
    };

    const unsubscribe = subscribe(messageHandler);
    return unsubscribe;
  }, [subscribe]);

  return {
    isConnected,
    isConnecting,
    received,
    sendMessage,
    subscribe,
    currentRoom: pageWebSocket.getCurrentRoom()
  };
};